import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";

const submissionSchema = z.object({
  submitter_name: z.string().min(1, "Name is required"),
  submitter_email: z.string().email("Valid email required"),
  title: z.string().min(5, "Title must be at least 5 characters"),
  description: z.string().min(20, "Please provide more detail (at least 20 characters)"),
});

type SubmissionForm = z.infer<typeof submissionSchema>;

interface SubmitChallengeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SubmitChallengeDialog: React.FC<SubmitChallengeDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { organization } = useOrganization();
  const form = useForm<SubmissionForm>({
    resolver: zodResolver(submissionSchema),
    defaultValues: {
      submitter_name: "",
      submitter_email: "",
      title: "",
      description: "",
    },
  });

  const onSubmit = async (data: SubmissionForm) => {
    try {
      const { error } = await supabase.from("challenge_submissions").insert({
        organization_id: organization?.id,
        submitter_name: data.submitter_name,
        submitter_email: data.submitter_email,
        title: data.title,
        description: data.description,
        status: "pending",
      });

      if (error) throw error;

      toast.success("Challenge idea submitted! We'll review it soon.");
      form.reset();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error submitting challenge:", error);
      toast.error("Failed to submit challenge idea");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Submit a Challenge Idea</DialogTitle>
          <DialogDescription>
            Have a problem the faith-tech community should solve? Submit your idea and we'll review it for inclusion.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="submitter_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="submitter_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Email</FormLabel>
                    <FormControl>
                      <Input placeholder="john@example.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Challenge Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Parish Volunteer Matching System" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the problem, who it affects, and potential solutions..."
                      rows={5}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                <Send className="h-4 w-4 mr-2" />
                {form.formState.isSubmitting ? "Submitting..." : "Submit Idea"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitChallengeDialog;
